import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import API_BASE_URL from "../../../../../Constants";

const SingleStepScrape = ({
  selectedDirectors,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  const [isScraping, setIsScraping] = useState(false);
  const [error, setError] = useState("");

  const [steps, setSteps] = useState([
    {
      name: "Authentication",
      status: "pending",
      description: "Log in each director & retrieve auth token.",
    },
    {
      name: "Devices",
      status: "pending",
      description: "Pull device data for the selected directors.",
    },
    {
      name: "Defects",
      status: "pending",
      description: "Collect defect data for those devices.",
    },
    {
      name: "Posting",
      status: "pending",
      description: "Create/Update inspections (DOB Now) & get summary.",
    },
  ]);

  const [devicesScraped, setDevicesScraped] = useState(0);
  const [defectsScraped, setDefectsScraped] = useState(0);
  const [inspectionSummary, setInspectionSummary] = useState(null);
  const [directorData, setDirectorData] = useState([]);

  // Dropdown states
  const [scrapeActionsOpen, setScrapeActionsOpen] = useState(false);
  const [dataActionsOpen, setDataActionsOpen] = useState(false);
  const [emailActionsOpen, setEmailActionsOpen] = useState(false);

  const resetPipeline = () => {
    setSteps((prev) => prev.map((step) => ({ ...step, status: "pending" })));
    setIsScraping(false);
    setError("");
    setDevicesScraped(0);
    setDefectsScraped(0);
    setInspectionSummary(null);
    setDirectorData([]);
  };

  const setStepStatus = (index, newStatus) => {
    setSteps((prev) => {
      const arr = [...prev];
      arr[index].status = newStatus;
      return arr;
    });
  };

  const handleClearResults = () => {
    if (isScraping) return;
    resetPipeline();
  };

  const handleFullScrape = async () => {
    if (isScraping) return;
    if (!startDate || !endDate) {
      alert("Please pick a start/end date first.");
      return;
    }
    if (!selectedDirectors || selectedDirectors.length === 0) {
      alert("Please select at least one director first.");
      return;
    }

    resetPipeline();
    setIsScraping(true);

    try {
      // STEP 0: Auth
      setStepStatus(0, "in-progress");
      const tempDirectorData = [];
      for (let i = 0; i < selectedDirectors.length; i++) {
        const dir = selectedDirectors[i];
        if (!dir.id || !dir.email) continue;

        const authRes = await fetch(`${API_BASE_URL}/api/inspections/posting/login`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            id: String(dir.id),
            email: dir.email,
          }),
          credentials: "include",
        });
        if (!authRes.ok) {
          throw new Error(`Auth failed for director #${dir.id}`);
        }
        const loginJson = await authRes.json();
        if (!loginJson.success) {
          throw new Error(`Auth error: ${loginJson.error}`);
        }

        tempDirectorData.push({
          ...dir,
          authToken: loginJson.session.authToken,
          systemUser: loginJson.session.contactGuid,
        });
      }
      setDirectorData(tempDirectorData);
      setStepStatus(0, "done");

      // STEP 1: Devices
      setStepStatus(1, "in-progress");
      let totalDevices = 0;
      for (let i = 0; i < tempDirectorData.length; i++) {
        const { id, authToken, systemUser } = tempDirectorData[i];
        if (!authToken || !systemUser) continue;

        const deviceRes = await fetch(
          `${API_BASE_URL}/api/admin/elevator-data?startDate=${startDate}&endDate=${endDate}&directorId=${id}`,
          {
            method: "GET",
            headers: { authToken, systemUser },
            credentials: "include",
          }
        );
        if (!deviceRes.ok) {
          throw new Error(`Devices fetch failed for director #${id}`);
        }
        const devices = await deviceRes.json();
        totalDevices += Array.isArray(devices) ? devices.length : 0;
      }
      setDevicesScraped(totalDevices);
      setStepStatus(1, "done");

      // STEP 2: Defects
      setStepStatus(2, "in-progress");
      let totalDefects = 0;
      for (let i = 0; i < tempDirectorData.length; i++) {
        const { authToken, systemUser } = tempDirectorData[i];
        if (!authToken || !systemUser) continue;

        const defectRes = await fetch(
          `${API_BASE_URL}/api/admin/scrapeDefects?startDate=${startDate}&endDate=${endDate}`,
          {
            method: "GET",
            headers: { authToken, systemUser },
            credentials: "include",
          }
        );
        if (!defectRes.ok) {
          throw new Error(`Defects fetch failed for director index ${i}`);
        }

        const defectData = await defectRes.json();
        let defectArray = [];
        if (Array.isArray(defectData)) {
          defectArray = defectData;
        } else if (defectData && Array.isArray(defectData.elv3Defects)) {
          defectArray = defectData.elv3Defects;
        }
        totalDefects += defectArray.length;
      }
      setDefectsScraped(totalDefects);
      setStepStatus(2, "done");

      // STEP 3: Post Inspections
      setStepStatus(3, "in-progress");
      const summaryRes = await fetch(`${API_BASE_URL}/api/scrape/dobnow`, {
        method: "POST",
        credentials: "include",
      });
      if (!summaryRes.ok) {
        throw new Error(`Failed to post inspections: status ${summaryRes.status}`);
      }
      const summaryData = await summaryRes.json();
      setInspectionSummary(summaryData);
      setStepStatus(3, "done");

    } catch (err) {
      console.error("Error in full scrape pipeline:", err);
      setError(err.message || "An error occurred during the scrape process.");
      setSteps(prevSteps =>
        prevSteps.map(step =>
          step.status === "in-progress" ? { ...step, status: "error" } : step
        )
      );
    } finally {
      setIsScraping(false);
    }
  };

  // The rest of the code stays the same, except we updated the "renderTimeline" container
  const getLastActiveStepIndex = () => {
    let lastActive = -1;
    steps.forEach((step, i) => {
      if (
        step.status === "in-progress" ||
        step.status === "done" ||
        step.status === "error"
      ) {
        lastActive = i;
      }
    });
    return lastActive;
  };

  const computeProgressPercent = () => {
    const idx = getLastActiveStepIndex();
    if (idx === -1) return 0;
    return (idx / (steps.length - 1)) * 100;
  };

  const getHighlightColor = () => {
    const idx = getLastActiveStepIndex();
    if (idx === -1) return "bg-transparent";
    const currentStatus = steps[idx].status;
    if (currentStatus === "done") return "bg-green-400";
    if (currentStatus === "error") return "bg-red-400";
    return "bg-blue-400";
  };

  const renderTimeline = () => {
    const progressPercent = computeProgressPercent();
    const highlightColor = getHighlightColor();

    return (
      <div className="relative w-full mt-4">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gray-300" />
        <div
          className={`absolute top-0 left-0 h-[3px] transition-all duration-300 ${highlightColor}`}
          style={{ width: `${progressPercent}%` }}
        />
        {/* Changed from "space-x-6" to "flex-wrap" with a smaller gap */}
        <div className="flex flex-wrap items-start pt-2 gap-2">
          {steps.map((step, idx) => {
            let labelColor = "text-gray-600 font-medium";
            if (step.status === "done") labelColor = "text-green-600 font-semibold";
            else if (step.status === "in-progress") labelColor = "text-blue-600 font-semibold";
            else if (step.status === "error") labelColor = "text-red-600 font-semibold";

            return (
              <Tooltip title={step.description || ""} arrow key={idx}>
                <div className={`text-xs ${labelColor} cursor-pointer`}>
                  {step.name}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  };

  const toggleScrapeActionsOpen = () => {
    if (isScraping) return;
    setScrapeActionsOpen(!scrapeActionsOpen);
  };
  const toggleDataActionsOpen = () => setDataActionsOpen(!dataActionsOpen);
  const toggleEmailActionsOpen = () => setEmailActionsOpen(!emailActionsOpen);

  return (
    <div className="w-full max-w-md mt-6 bg-white space-y-4">
      {/* Title row */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-700">Single-Step Scrape</h2>
        <Tooltip title="Auth + Devices + Defects + Post Inspections" arrow>
          <InfoOutlined className="text-gray-500 cursor-pointer" fontSize="small" />
        </Tooltip>
      </div>

      {/* Error */}
      {error && (
        <div className="p-2 bg-red-100 text-red-700 border border-red-300 rounded">
          {error}
        </div>
      )}

      {/* Directors */}
      <div className="bg-gray-50 p-2 rounded border text-sm text-gray-700">
        <strong>Selected Director IDs:</strong>{" "}
        {selectedDirectors.map((d) => d.id).join(", ")}
      </div>

      {/* Dates */}
      <div className="flex flex-wrap gap-2">
        <div className="flex-1 min-w-[120px] space-y-1">
          <label className="block text-sm font-bold text-gray-700">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={isScraping}
            className={`w-full px-2 py-1 text-sm text-gray-700 border rounded focus:outline-none ${
              isScraping ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </div>
        <div className="flex-1 min-w-[120px] space-y-1">
          <label className="block text-sm font-bold text-gray-700">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={isScraping}
            className={`w-full px-2 py-1 text-sm text-gray-700 border rounded focus:outline-none ${
              isScraping ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          />
        </div>
      </div>

      {/* Timeline */}
      {renderTimeline()}

      {/* Summary */}
      <div className="bg-gray-50 p-2 rounded border text-sm text-gray-700">
        <div className="grid grid-cols-4 gap-4 items-start">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Inspections Created
            </label>
            <input
              type="text"
              readOnly
              value={inspectionSummary?.createdInspections ?? 0}
              className="w-full px-2 py-1 text-sm text-gray-700 border rounded focus:outline-none bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Inspections Updated
            </label>
            <input
              type="text"
              readOnly
              value={inspectionSummary?.updatedInspections ?? 0}
              className="w-full px-2 py-1 text-sm text-gray-700 border rounded focus:outline-none bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Devices Created
            </label>
            <input
              type="text"
              readOnly
              value={inspectionSummary?.createdInspectionDevices ?? 0}
              className="w-full px-2 py-1 text-sm text-gray-700 border rounded focus:outline-none bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Violations Created
            </label>
            <input
              type="text"
              readOnly
              value={inspectionSummary?.createdViolations ?? 0}
              className="w-full px-2 py-1 text-sm text-gray-700 border rounded focus:outline-none bg-white"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        {/* Scrape Actions */}
        <div>
          <button
            onClick={toggleScrapeActionsOpen}
            disabled={isScraping}
            className={`
              w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-md
              text-gray-700 font-semibold transition focus:outline-none
              ${
                isScraping
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-gray-100 hover:bg-gray-200"
              }
            `}
          >
            <span>Start Scrape Actions</span>
            <svg
              className={`h-5 w-5 transform transition-transform ${
                scrapeActionsOpen ? "rotate-90" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {scrapeActionsOpen && (
            <div className="border border-gray-200 rounded-md mt-1 bg-white shadow-sm">
              <ul className="flex flex-col text-sm text-gray-800">
                <li
                  className={`
                    px-4 py-2 hover:bg-gray-50
                    ${isScraping ? "opacity-50 cursor-not-allowed text-gray-400" : "cursor-pointer"}
                  `}
                  onClick={() => {
                    if (!isScraping) {
                      handleFullScrape();
                      setScrapeActionsOpen(false);
                    }
                  }}
                >
                  Run Full Scrape
                </li>
                <li
                  className={`
                    px-4 py-2 hover:bg-gray-50
                    ${isScraping ? "opacity-50 cursor-not-allowed text-gray-400" : "cursor-pointer"}
                  `}
                  onClick={() => {
                    if (!isScraping) {
                      handleClearResults();
                      setScrapeActionsOpen(false);
                    }
                  }}
                >
                  Clear Results
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Data Actions */}
        <div>
          <button
            onClick={toggleDataActionsOpen}
            className="
              w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-md
              bg-gray-100 text-gray-700 font-semibold transition hover:bg-gray-200 focus:outline-none
            "
          >
            <span>Data Actions</span>
            <svg
              className={`h-5 w-5 transform transition-transform ${
                dataActionsOpen ? "rotate-90" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {dataActionsOpen && (
            <div className="border border-gray-200 rounded-md mt-1 bg-white shadow-sm">
              <ul className="flex flex-col text-sm text-gray-800">
                <li className="px-4 py-2 hover:bg-gray-50 cursor-pointer">
                  Export Devices
                </li>
                <li className="px-4 py-2 hover:bg-gray-50 cursor-pointer">
                  Export Defects
                </li>
                <li className="px-4 py-2 hover:bg-gray-50 cursor-pointer">
                  Import CSV
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Email Actions */}
        <div>
          <button
            onClick={toggleEmailActionsOpen}
            className="
              w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-md
              bg-gray-100 text-gray-700 font-semibold transition hover:bg-gray-200 focus:outline-none
            "
          >
            <span>Email Actions</span>
            <svg
              className={`h-5 w-5 transform transition-transform ${
                emailActionsOpen ? "rotate-90" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {emailActionsOpen && (
            <div className="border border-gray-200 rounded-md mt-1 bg-white shadow-sm">
              <ul className="flex flex-col text-sm text-gray-800">
                <li className="px-4 py-2 hover:bg-gray-50 cursor-pointer">
                  Send Email
                </li>
                <li className="px-4 py-2 hover:bg-gray-50 cursor-pointer">
                  Create Email List
                </li>
                <li className="px-4 py-2 hover:bg-gray-50 cursor-pointer">
                  Excel Export
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleStepScrape;
